import { createServerFn } from '@tanstack/react-start';
import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';
import { multiSearchDeals, tsEsc } from '@/lib/typesense.server';

export type ProductCategory = { id: string; slug: string; name: string; parent_id: string | null; seo_title: string | null; seo_description: string | null; intro: string | null; sort_order: number };
export type CategoryRef = { slug: string; name: string; parent: { slug: string; name: string } | null };
export type ProductTile = {
  slug: string; title: string; image_url: string | null; price: number; original_price: number | null;
  discount_percent: number | null; store: string | null; stores: number | null;
};
export type DealDetail = {
  id: string; slug: string; title: string; image_url: string; price: number; original_price: number; discount_percent: number;
  category: string | null; deeplink: string; gtin: string | null; available: boolean; checked_at: string; brand_id: string;
  brand: { id: string; name: string; slug: string; logo_url: string | null; commission_type: string | null; donation_percentage: number | null; affiliate_network: string | null };
};
export type ProductContent = { intro: string; highlights: string[]; audience: string | null; faq: { q: string; a: string }[] };
export type CatLink = { slug: string; name: string; url: string };
export type ProductExtras = { content: ProductContent | null; siblings: CatLink[] };
export type ProductResult =
  | { kind: 'redirect'; slug: string }
  | ({ kind: 'comparison'; id: string; slug: string; category: CategoryRef | null; similar: ProductTile[] } & ProductExtras)
  | ({ kind: 'deal'; deal: DealDetail; category: CategoryRef | null; similar: ProductTile[]; sameStore: ProductTile[]; stale: boolean; variants: { id: string; title: string; price: number; deeplink: string | null; available: boolean; size: string | null; color: string | null }[]; history: { day: string; price: number }[] } & ProductExtras)
  | { kind: 'none' };

function publicDb() {
  const key = process.env['SUPABASE_PUBLISHABLE_KEY'];
  const url = process.env['SUPABASE_URL'];
  if (!key || !url) return null;
  return createClient<Database>(url, key, {
    auth: { persistSession: false },
    global: { fetch: (input, init) => {
      const headers = new Headers(init?.headers);
      if (key.startsWith('sb_') && headers.get('Authorization') === `Bearer ${key}`) headers.delete('Authorization');
      headers.set('apikey', key);
      return fetch(input, { ...init, headers });
    } },
  }) as any;
}

const TILE_DEAL = 'slug,title,image_url,price,original_price,discount_percent,brand:brands!inner(name)';
const dealTile = (r: any): ProductTile => ({ slug: r.slug, title: r.title, image_url: r.image_url, price: Number(r.price), original_price: Number(r.original_price), discount_percent: r.discount_percent, store: r.brand?.name ?? null, stores: null });
const fresh = () => new Date(Date.now() - 10 * 86400_000).toISOString();

async function categoryRef(db: any, id: string | null): Promise<CategoryRef | null> {
  if (!id) return null;
  const { data: c } = await db.from('product_categories').select('slug,name,parent_id').eq('id', id).maybeSingle();
  if (!c) return null;
  if (!c.parent_id) return { slug: c.slug, name: c.name, parent: null };
  const { data: p } = await db.from('product_categories').select('slug,name').eq('id', c.parent_id).maybeSingle();
  return { slug: c.slug, name: c.name, parent: p ? { slug: p.slug, name: p.name } : null };
}

async function extras(db: any, slug: string, category: CategoryRef | null): Promise<ProductExtras> {
  const [{ data: c }, siblings] = await Promise.all([
    db.from('product_content').select('intro,highlights,audience,faq').eq('slug', slug).maybeSingle(),
    siblingLinks(db, category),
  ]);
  return { content: c ? { intro: c.intro, highlights: c.highlights ?? [], audience: c.audience, faq: c.faq ?? [] } : null, siblings };
}

// Categorias irmãs com ofertas no ar (sem links para páginas vazias).
async function siblingLinks(db: any, category: CategoryRef | null): Promise<CatLink[]> {
  if (!category) return [];
  const parentSlug = category.parent?.slug ?? category.slug;
  const { data: parent } = await db.from('product_categories').select('id').eq('slug', parentSlug).is('parent_id', null).maybeSingle();
  if (!parent) return [];
  const [{ data: subs }, { data: facets }] = await Promise.all([
    db.from('product_categories').select('id,slug,name').eq('parent_id', parent.id).order('sort_order'),
    db.rpc('deal_facets', { _brand: null, _cats: null }),
  ]);
  const live = new Set((facets ?? []).filter((f: any) => f.n > 0).map((f: any) => f.category_id));
  return (subs ?? []).filter((s: any) => live.has(s.id) && s.slug !== category.slug).slice(0, 10)
    .map((s: any) => ({ slug: s.slug, name: s.name, url: `/ofertas/${parentSlug}/${s.slug}` }));
}

const similarityQuery = (title: string) => title
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  .replace(/\b(kit|combo|pack|com|para|de|da|do|das|dos|e|em|unidades?|produto|original)\b/g, ' ')
  .replace(/\b\d+(?:ml|l|kg|g|cm|mm|v|w)?\b/g, ' ')
  .replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim().split(' ').filter(x => x.length > 2).slice(-3).join(' ');

async function similarTiles(db: any, categoryId: string | null, price: number, excludeSlug: string, title: string): Promise<ProductTile[]> {
  if (!categoryId) return [];
  const q = similarityQuery(title);
  if (q) {
    const filters = [`category_id:=${tsEsc(categoryId)}`, 'available:=true', 'image_missing:=false', `price:>=${Math.max(0.01, price * 0.55)}`, `price:<=${price * 1.8}`];
    const result = await multiSearchDeals([{ q, query_by: 'title,product_brand', filter_by: filters.join(' && '), sort_by: '_text_match:desc,discount_percent:desc', per_page: 30 }]);
    const docs = (result?.[0]?.hits ?? []).map((h: any) => h.document).filter((d: any) => d.slug && d.slug !== excludeSlug);
    if (docs.length) {
      const uniqueStores = new Set<string>();
      const first = docs.filter((d: any) => { if (uniqueStores.has(d.brand_id)) return false; uniqueStores.add(d.brand_id); return true; });
      const firstIds = new Set(first.map((d: any) => d.id));
      const ordered = [...first, ...docs.filter((d: any) => !firstIds.has(d.id))];
      return ordered.slice(0, 8).map((d: any) => ({ slug: d.slug, title: d.title, image_url: d.image_url ?? null, price: Number(d.price), original_price: d.original_price == null ? null : Number(d.original_price), discount_percent: d.discount_percent ?? null, store: d.brand_name ?? null, stores: null }));
    }
  }
  // RPC limitada: lê no máximo 300 produtos da faixa de preço pelo índice
  // (categoria, preço) e só então ordena por desconto. A consulta antiga
  // varria a categoria inteira e chegava a 8 s com o catálogo grande.
  const [{ data: deals }, { data: cmps }] = await Promise.all([
    db.rpc('similar_deal_tiles', { p_category: categoryId, p_price: price, p_exclude: excludeSlug, p_limit: 8 }),
    db.from('comparison_products').select('slug,title,image_url,min_price,store_count').eq('review_status', 'approved').gte('store_count', 2)
      .eq('category_id', categoryId).neq('slug', excludeSlug).gte('min_price', price * 0.6).lte('min_price', price * 1.6).limit(4),
  ]);
  const c: ProductTile[] = (cmps ?? []).map((r: any) => ({ slug: r.slug, title: r.title, image_url: r.image_url, price: Number(r.min_price), original_price: null, discount_percent: null, store: null, stores: r.store_count }));
  return [...c, ...(deals ?? []).map(dealTile)].slice(0, 8);
}

// POST: o proxy da borda repassa POST direto ao servidor.
export const getProduct = createServerFn({ method: 'POST' })
  .inputValidator((input: { slug: string }) => ({ slug: String(input?.slug ?? '').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 120) }))
  .handler(async ({ data }): Promise<ProductResult> => {
    const db = publicDb();
    if (!db || !data.slug) return { kind: 'none' };
    // URLs de produto nunca somem: esgotado mantém a mesma página, histórico e link de afiliado.
    const { data: cmp } = await db.from('comparison_products').select('id,slug,title,category_id,min_price,store_count,review_status').eq('slug', data.slug).maybeSingle();
    if (cmp && cmp.review_status === 'approved' && (cmp.store_count ?? 0) >= 2) {
      const { data: live } = await (db as any).from('comparison_offers').select('brand_id').eq('product_id', cmp.id).eq('available', true).gt('expires_at', new Date().toISOString()).limit(50);
      if (new Set((live ?? []).map((o: any) => o.brand_id)).size >= 2) {
        const [category, similar] = await Promise.all([categoryRef(db, cmp.category_id), similarTiles(db, cmp.category_id, Number(cmp.min_price) || 0, cmp.slug, cmp.title ?? cmp.slug)]);
        return { kind: 'comparison', id: cmp.id, slug: cmp.slug, category, similar, ...(await extras(db, cmp.slug, category)) };
      }
    }
    const SEL = 'id,slug,title,comparison_product_id,image_url,price,original_price,discount_percent,category,category_id,deeplink,gtin,available,checked_at,brand_id,parent_deal_id,cmp:comparison_products(slug,store_count,review_status),brand:brands!inner(id,name,slug,logo_url,commission_type,donation_percentage,affiliate_network)';
    let { data: d } = await db.from('deal_products').select(SEL).eq('slug', data.slug).maybeSingle();
    let keepSlug: string | null = null;
    if (!d && cmp) {
      // Comparação sem lojas suficientes: mostra a melhor oferta (mesmo esgotada) na própria URL.
      const { data: alt } = await db.from('deal_products').select(SEL).eq('comparison_product_id', cmp.id).is('parent_deal_id', null).order('available', { ascending: false }).order('checked_at', { ascending: false }).limit(1);
      d = alt?.[0] ?? null; keepSlug = cmp.slug;
    }
    if (d && !/amazon/i.test(d.brand?.name ?? '')) {
      if (!keepSlug && d.parent_deal_id) { const { data: par } = await db.from('deal_products').select('slug').eq('id', d.parent_deal_id).maybeSingle(); if (par?.slug) return { kind: 'redirect', slug: par.slug }; }
      if (!keepSlug && d.cmp?.slug && d.cmp.slug !== data.slug && d.cmp.review_status === 'approved' && d.cmp.store_count >= 2) {
        const { data: live } = await (db as any).from('comparison_offers').select('brand_id').eq('product_id', (d as any).comparison_product_id ?? '00000000-0000-0000-0000-000000000000').eq('available', true).gt('expires_at', new Date().toISOString()).limit(50);
        if (d.available && new Set((live ?? []).map((o: any) => o.brand_id)).size >= 2) return { kind: 'redirect', slug: d.cmp.slug };
      }
      const stale = !d.available || Date.parse(d.checked_at) < Date.now() - 10 * 86400_000;
      const [category, similar, { data: same }, { data: hist }, { data: vars }] = await Promise.all([
        categoryRef(db, d.category_id),
        similarTiles(db, d.category_id, Number(d.price), d.slug, d.title),
        db.from('deal_products').select(TILE_DEAL).eq('brand_id', d.brand_id).eq('available', true).is('parent_deal_id', null).gte('checked_at', fresh()).neq('slug', d.slug).order('discount_percent', { ascending: false }).limit(8),
        db.from('deal_price_history').select('day,price').eq('deal_id', d.id).gte('day', new Date(Date.now() - 180 * 86400_000).toISOString().slice(0, 10)).order('day'),
        db.from('deal_products').select('id,title,price,deeplink,available,size,color').eq('parent_deal_id', d.id).order('price').limit(60),
      ]);
      const { cmp: _c, category_id: _ci, ...rest } = d;
      const slug = keepSlug ?? d.slug;
      return { kind: 'deal', deal: { ...rest, slug, price: Number(d.price), original_price: Number(d.original_price) }, category, similar, sameStore: (same ?? []).map(dealTile), stale,
        variants: (vars ?? []).map((v: any) => ({ ...v, price: Number(v.price) })), history: (hist ?? []).map((h: any) => ({ day: h.day, price: Number(h.price) })), ...(await extras(db, slug, category)) };
    }
    return { kind: 'none' };
  });

export const listProductCategories = createServerFn({ method: 'POST' })
  .handler(async (): Promise<ProductCategory[]> => {
    const db = publicDb();
    if (!db) return [];
    const { data } = await db.from('product_categories').select('id,slug,name,parent_id,seo_title,seo_description,intro,sort_order').order('sort_order');
    return data ?? [];
  });
